/**
 * Seeded deterministic RNG (sfc32, seeded with splitmix32). Uses only 32-bit integer math, so it
 * produces identical sequences on every JS engine. The simulation must never use Math.random.
 */
export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [min, max]. */
  int(min: number, max: number): number;
  /** Snapshot of the internal state, for replays and reconciliation. */
  state(): [number, number, number, number];
}

function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
    return (z ^ (z >>> 16)) >>> 0;
  };
}

export function createRng(seed: number, from?: [number, number, number, number]): Rng {
  let a: number;
  let b: number;
  let c: number;
  let d: number;
  if (from) {
    [a, b, c, d] = from;
  } else {
    const sm = splitmix32(seed);
    a = sm();
    b = sm();
    c = sm();
    d = sm();
  }

  const nextU32 = (): number => {
    const t = (((a + b) >>> 0) + d) >>> 0;
    d = (d + 1) >>> 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) >>> 0;
    c = ((c << 21) | (c >>> 11)) >>> 0;
    c = (c + t) >>> 0;
    return t;
  };

  return {
    next: () => nextU32() / 4294967296,
    int: (min, max) => min + Math.floor((nextU32() / 4294967296) * (max - min + 1)),
    state: () => [a, b, c, d],
  };
}
